import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {
  DtcpAccelerometerModule,
  PModuleMeasurement,
  DtcpAccelerometerFrequencyAndBandwidth,
} from '@/views/system/modules/interfaces';
import {PModuleType} from '@/enums/configs';
import {
  PMProcessActionType,
  PMProcessStatus,
  PMProcessSystemType,
} from '@/views/system/modules/enums';
import {EntityModule} from '@/store/modules/entities/entityModule';
import {httpClient, openEventSource} from '@/api/baseApi';
import {DateTimeInterval} from '@/utils/dateTimeItervalUtils';
import {
  getEventData,
  getEventTypes,
  getLastEvents,
  getSignal,
  getSpectrum,
  getRawData,
} from '@/store/modules/entities/pmodules/base';
import {
  DtcpAcceleromerModuleAxisType,
  SpectrumEventType,
  SpectrumIntervalAggregateType,
  SpectrumYAxisType,
} from '@/enums/diagrams';
import {PModuleSpectrumStoreModule} from '@/store/modules/entities/pmodules/interfaces';
import {isNumber} from '@/utils/commonUtils';

/*
    Динамический модуль хранилища vuex для работы с ПМ датчика вибрации 3-х осевой СВМ/WiFiSensor протокола ПСДУ
*/

const moduleName = 'dtcpAccelerometerStoreModule';

function transformGet(elem: any): DtcpAccelerometerModule {
  return {
    id: elem.id,
    name: elem.name,
    guid: elem.guid,
    description: elem.description,
    processStatus: elem.process_status as PMProcessStatus,
    processStatusDescription: elem.process_status_description,
    status: elem.status,
    nodes: elem.nodes,
    alias: elem.alias,
    type: PModuleType.DtcpAccelerometerModule,
    device: elem.device,
    stat_channel: elem.stat_channel,
    sensor_type: elem.sensor_type,
    amp_spectr_channel: elem.amp_spectr_channel,
    power_spectr_channel: elem.power_spectr_channel,
    envelope_spectr_channel: elem.envelope_spectr_channel,
    octave_spectr_channel: elem.octave_spectr_channel,
    velocity_stat_channel: elem.velocity_stat_channel,
    raw_signal_channel: elem.raw_signal_channel,
    displacement_stat_channel: elem.displacement_stat_channel,
    accel_start_freq: elem.accel_start_freq,
    accel_end_freq: elem.accel_end_freq,
    velocity_start_freq: elem.velocity_start_freq,
    velocity_end_freq: elem.velocity_end_freq,
    displacement_start_freq: elem.displacement_start_freq,
    displacement_end_freq: elem.displacement_end_freq,
    envelope_3doctave: elem.envelope_3doctave,
    envelope_1doctave: elem.envelope_1doctave,
    sync_channel: elem.sync_channel,
    meas_time_s: elem.meas_time_s,
    fft_types: elem.fft_types,
    default_spectrum_count: elem.default_spectrum_count,
    rms_x_threshold: elem.rms_x_threshold,
    rms_y_threshold: elem.rms_y_threshold,
    rms_z_threshold: elem.rms_z_threshold,
    rms_spectrum_count: elem.rms_spectrum_count,
    taho_guid: elem.taho_guid,
    taho_spectrum_count: elem.taho_spectrum_count,
    taho_threshold: elem.taho_threshold,
    energy_guid: elem.energy_guid,
    energy_spectrum_count: elem.energy_spectrum_count,
    energy_threshold: elem.energy_threshold,
    parent_pm: elem.parent_pm ?? null,
    measurements: [],
    freqbands: [],
    span_nodes: elem.span_nodes,
  };
}

function getMeasurements(elem: any): PModuleMeasurement[] {
  return elem.measurements.map((elem: any) => ({
    measurementId: elem.module_type_measurement_id,
    measurementDescription: elem.module_type_measurement_description,
    topWarningBoundary: elem.top_warning_boundary,
    topDangerBoundary: elem.top_danger_boundary,
    topErrorBoundary: elem.top_error_boundary,
  }));
}

function getFreqbands(elem: any): DtcpAccelerometerFrequencyAndBandwidth[] {
  return elem.freqbands.map((elem: any, index: number) => ({
    id: index + 1,
    central_frequency: elem.central_frequency,
    bandwidth: elem.bandwidth,
  }));
}

function transformGetById(elem: any): DtcpAccelerometerModule {
  const model = transformGet(elem);
  model.measurements = getMeasurements(elem);
  model.freqbands = getFreqbands(elem);
  return model;
}

function transformCreateEdit(elem: any): DtcpAccelerometerModule {
  const measurements: PModuleMeasurement[] = getMeasurements(elem);
  const freqbands: DtcpAccelerometerFrequencyAndBandwidth[] = getFreqbands(elem);
  return {
    name: elem.name,
    alias: elem.alias,
    description: elem.description,
    nodes: elem.nodes,
    type: PModuleType.DtcpAccelerometerModule,
    device: elem.device,
    stat_channel: elem.stat_channel,
    sensor_type: elem.sensor_type,
    amp_spectr_channel: elem.amp_spectr_channel,
    power_spectr_channel: elem.power_spectr_channel,
    envelope_spectr_channel: elem.envelope_spectr_channel,
    octave_spectr_channel: elem.octave_spectr_channel,
    velocity_stat_channel: elem.velocity_stat_channel,
    accel_start_freq: elem.accel_start_freq,
    accel_end_freq: elem.accel_end_freq,
    velocity_start_freq: elem.velocity_start_freq,
    velocity_end_freq: elem.velocity_end_freq,
    raw_signal_channel: elem.raw_signal_channel,
    displacement_start_freq: elem.displacement_start_freq,
    displacement_end_freq: elem.displacement_end_freq,
    displacement_stat_channel: elem.displacement_stat_channel,
    envelope_3doctave: elem.envelope_3doctave,
    envelope_1doctave: elem.envelope_1doctave,
    sync_channel: elem.sync_channel,
    meas_time_s: elem.meas_time_s,
    fft_types: elem.fft_types,
    default_spectrum_count: elem.default_spectrum_count,
    rms_x_threshold: elem.rms_x_threshold,
    rms_y_threshold: elem.rms_y_threshold,
    rms_z_threshold: elem.rms_z_threshold,
    rms_spectrum_count: elem.rms_spectrum_count,
    taho_guid: elem.taho_guid,
    taho_spectrum_count: elem.taho_spectrum_count,
    taho_threshold: elem.taho_threshold,
    energy_guid: elem.energy_guid,
    energy_spectrum_count: elem.energy_spectrum_count,
    energy_threshold: elem.energy_threshold,
    parent_pm: elem.parent_pm ?? null,
    measurements: measurements,
    freqbands: freqbands,
    guid: elem.guid,
  };
}

function transformSave(model: any): any {
  const measurements: any[] = model.measurements.map((elem: any) => {
    const m: any = {
      module_type_measurement_id: elem.measurementId,
      module_type_measurement_description: elem.measurementDescription,
    };
    if (isNumber(elem.topWarningBoundary)) m.top_warning_boundary = elem.topWarningBoundary;
    else {
      const num = Number(elem.topWarningBoundary);
      if (isNumber(num)) m.top_warning_boundary = num;
    }
    if (isNumber(elem.topDangerBoundary)) m.top_danger_boundary = elem.topDangerBoundary;
    else {
      const num = Number(elem.topDangerBoundary);
      if (isNumber(num)) m.top_danger_boundary = num;
    }
    if (isNumber(elem.topErrorBoundary)) m.top_error_boundary = elem.topErrorBoundary;
    else {
      const num = Number(elem.topErrorBoundary);
      if (isNumber(num)) m.top_error_boundary = num;
    }
    return m;
  });

  const freqbands: any[] = model.freqbands.map((elem: any) => {
    const item: any = {
      central_frequency: elem.central_frequency,
      bandwidth: elem.bandwidth,
    };
    return item;
  });

  return {
    name: model.name,
    description: model.description,
    node_id: model.nodeId ? model.nodeId : model.node_id,
    device: model.device,
    stat_channel: model.stat_channel,
    amp_spectr_channel: model.amp_spectr_channel,
    power_spectr_channel: model.power_spectr_channel,
    envelope_spectr_channel: model.envelope_spectr_channel,
    octave_spectr_channel: model.octave_spectr_channel,
    velocity_stat_channel: model.velocity_stat_channel,
    accel_start_freq: model.accel_start_freq,
    accel_end_freq: model.accel_end_freq,
    velocity_start_freq: model.velocity_start_freq,
    velocity_end_freq: model.velocity_end_freq,
    raw_signal_channel: model.raw_signal_channel,
    displacement_start_freq: model.displacement_start_freq,
    displacement_end_freq: model.displacement_end_freq,
    displacement_stat_channel: model.displacement_stat_channel,
    envelope_3doctave: model.envelope_3doctave,
    envelope_1doctave: model.envelope_1doctave,
    sync_channel: model.sync_channel,
    sensor_type: model.sensor_type,
    meas_time_s: model.meas_time_s,
    fft_types: model.fft_types,
    default_spectrum_count: model.default_spectrum_count,
    rms_x_threshold: model.rms_x_threshold,
    rms_y_threshold: model.rms_y_threshold,
    rms_z_threshold: model.rms_z_threshold,
    rms_spectrum_count: model.rms_spectrum_count,
    taho_guid: model.taho_guid,
    taho_spectrum_count: model.taho_spectrum_count,
    taho_threshold: model.taho_threshold,
    energy_guid: model.energy_guid,
    energy_spectrum_count: model.energy_spectrum_count,
    energy_threshold: model.energy_threshold,
    parent_pm: model.parent_pm,
    measurements: measurements,
    freqbands: freqbands,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class DtcpAccelerometerStoreModule extends EntityModule
  implements PModuleSpectrumStoreModule {
  protected routeName = 'dtcp-accelerometer-modules';

  get all(): DtcpAccelerometerModule[] {
    return super.all as DtcpAccelerometerModule[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: DtcpAccelerometerModule}> {
    return super.create(transformCreateEdit) as Promise<{data: DtcpAccelerometerModule}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: DtcpAccelerometerModule}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: DtcpAccelerometerModule}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: DtcpAccelerometerModule) {
    return super.save(model, transformSave);
  }

  //Получение объекта по идентификатору
  @Action
  async getById(id: any): Promise<{data: DtcpAccelerometerModule}> {
    return super.getById(id, transformGetById) as Promise<{data: DtcpAccelerometerModule}>;
  }

  /* TODO: Данные методы не вынести в базовый класс (т. к. в store наследование больше 2хмодулей не работает) */

  //Получение сигнала
  @Action
  async getSignal(params: {
    id: number;
    dateInterval: DateTimeInterval;
    fieldAlias: string;
    statType?: string;
  }): Promise<any> {
    try {
      return await getSignal(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение спектра
  @Action
  async getSpectrum(params: {
    id: number;
    spectrumType: SpectrumEventType;
    date?: number;
    offset?: number;
    length?: number;
    spectrumValueType?: SpectrumYAxisType;
    fieldAlias?: string;
    central_frequency?: number;
    bandwidth?: number;
  }): Promise<any> {
    try {
      return await getSpectrum(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение сырых данных
  @Action
  async getRawData(params: {
    id: number;
    fieldAlias: string;
    date?: number;
    offset?: number;
    length?: number;
  }): Promise<any> {
    try {
      return await getRawData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Выполнить действие
  @Action
  async processAction(params: {id: number; action: PMProcessActionType}): Promise<any> {
    try {
      const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/`, {
        action: params.action,
      });
      return data;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Выполнить системную команду программного модуля
  @Action
  async processSystem(params: {id: number; action: PMProcessSystemType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/system`, {
      action: params.action,
    });
    return data;
  }

  //Получение типов событий
  @Action
  async getEventTypes(): Promise<any> {
    try {
      return await getEventTypes(this.routeName);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение списка последних событий
  @Action
  async getLastEvents(params: {
    id: number;
    eventType: string;
    limit?: number;
    startDate?: number;
    endDate?: number;
  }): Promise<any> {
    try {
      return await getLastEvents(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение данных события
  @Action
  async getEventData(params: {id: number; eventType: string; timestamp: number}): Promise<any> {
    try {
      return await getEventData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Запуск расчета интервального спектра
  @Action
  async runIntervalSpectrumTask(params: {
    id: number;
    startDate: number;
    endDate: number;
    spectrumType: SpectrumEventType;
    aggregateType: SpectrumIntervalAggregateType;
    heightToKeep: number;
    widthToKeep: number;
    axisType?: DtcpAcceleromerModuleAxisType;
    offset?: number;
    length?: number;
    thiningFactor?: number;
    parallelFactor?: number;
  }): Promise<any> {
    try {
      return await httpClient.get(`${this.routeName}/${params.id}/tasks/spectrum_interval/run/`, {
        params: {
          start_date: params.startDate,
          end_date: params.endDate,
          spectrum_type: params.spectrumType,
          aggregate_type: params.aggregateType,
          height_to_keep: params.heightToKeep,
          width_to_keep: params.widthToKeep,
          offset: params.offset,
          length: params.length,
          thining_factor: params.thiningFactor,
          parallel_factor: params.parallelFactor,
          axis_type: params.axisType,
        },
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получить статус расчета интервального спектра (отдельно от SSE)
  @Action
  async getIntervalSpectrumTaskStatus(params: {id: number; taskCode: string}): Promise<any> {
    try {
      return await httpClient.get(
        `${this.routeName}/${params.id}/tasks/spectrum_interval/${params.taskCode}/status/`,
      );
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получить статус расчета интевального спектра (SSE)
  @Action
  async getIntervalSpectrumTaskStatusSSE(params: {id: number; taskCode: string}) {
    return openEventSource(
      `${this.routeName}/${params.id}/tasks/spectrum_interval/${params.taskCode}/status/`,
    );
  }

  //Остановить расчет интервального спектра
  @Action
  async stopIntervalSpectrumTask(params: {id: number; taskCode: string}): Promise<any> {
    try {
      return await httpClient.put(
        `${this.routeName}/${params.id}/tasks/spectrum_interval/${params.taskCode}/cancel/`,
      );
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получить результат расчета интервального спектра
  @Action
  async getIntervalSpectrumTaskResult(params: {
    id: number;
    taskCode: string;
    axisType: SpectrumYAxisType;
    spectrumEventType: SpectrumEventType;
  }): Promise<any> {
    try {
      const parameters = {
        spectrum_event_type: params.spectrumEventType,
        spectrum_value_type: params.axisType,
      };
      return await httpClient.get(
        `${this.routeName}/${params.id}/tasks/spectrum_interval/${params.taskCode}/result/`,
        {params: parameters},
      );
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(DtcpAccelerometerStoreModule);
